import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { API, formatApiErrorDetail } from "../lib/api";
import { TopBar } from "../components/TopBar";
import { ClipPlayer } from "../components/ClipPlayer";
import { DualClipPlayer } from "../components/DualClipPlayer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  ArrowLeft, Download, Loader2, Swords, Shield, Trash2, Film,
  Video, PlayCircle, Star, Radio, StickyNote, Check, Goal, Flag, Layers, DownloadCloud, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

const FILTERS = [
  { key: "all", label: "All", icon: Film },
  { key: "offense", label: "Offense", icon: Swords },
  { key: "defense", label: "Defense", icon: Shield },
];

function PlayCard({ play, active, isNew, downloaded, onClick, onDelete, onStar }) {
  const isOff = play.unit === "offense";
  return (
    <div data-testid={`play-card-${play.play_number}`} onClick={onClick}
      className={`group relative cursor-pointer rounded-md overflow-hidden border-2 bg-card transition-all duration-200 ${
        active ? "border-white" : isNew ? "border-success" : "border-border hover:border-muted-foreground/50"}`}>
      {isNew && <div className="absolute top-2 left-1/2 -translate-x-1/2 z-10 px-2 py-0.5 rounded bg-success text-black text-[10px] font-black uppercase tracking-widest">New</div>}
      <div className="aspect-video bg-black flex items-center justify-center relative">
        <div className={`absolute top-2 left-2 h-9 w-9 rounded flex items-center justify-center font-heading text-lg font-black tabular-nums ${isOff ? "bg-primary text-white" : "bg-secondary text-black"}`}>
          {play.play_number}
        </div>
        <span className="absolute top-2 right-2 px-1.5 py-0.5 rounded bg-black/70 text-[10px] font-bold uppercase tracking-widest text-white">Q{play.quarter}</span>
        <PlayCircle className="h-10 w-10 text-white/40 group-hover:text-white/80 transition-colors" strokeWidth={1.5} />
        {/* angle badges */}
        <div className="absolute bottom-2 left-2 flex items-center gap-1">
          {play.sideline && <span className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-black/70 text-[10px] font-bold uppercase tracking-widest text-white"><Flag className="h-3 w-3" strokeWidth={2.75} />SL</span>}
          {play.endzone && <span className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-black/70 text-[10px] font-bold uppercase tracking-widest text-white"><Goal className="h-3 w-3" strokeWidth={2.75} />EZ</span>}
          {play.sideline && play.endzone && <span className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-success/90 text-black text-[10px] font-black uppercase tracking-widest"><Layers className="h-3 w-3" strokeWidth={2.75} />2</span>}
        </div>
        {play.note && <StickyNote className="absolute bottom-2 right-2 h-4 w-4 text-secondary" strokeWidth={2.5} />}
      </div>
      <div className="flex items-center justify-between px-2.5 py-2">
        <span className={`text-xs font-bold uppercase tracking-widest ${isOff ? "text-primary" : "text-secondary"}`}>{play.unit}</span>
        <div className="flex items-center gap-2">
          {downloaded && <span data-testid={`downloaded-${play.play_number}`} title="Saved for instant playback"><CheckCircle2 className="h-4 w-4 text-secondary" strokeWidth={2.5} /></span>}
          <button data-testid={`star-play-${play.play_number}`} onClick={(e) => { e.stopPropagation(); onStar(play); }}
            className={`transition-colors ${play.starred ? "text-yellow-400" : "text-muted-foreground hover:text-yellow-400"}`}>
            <Star className={`h-4 w-4 ${play.starred ? "fill-yellow-400" : ""}`} strokeWidth={2.25} />
          </button>
          <button data-testid={`delete-play-${play.play_number}`} onClick={(e) => { e.stopPropagation(); onDelete(play); }}
            className="text-muted-foreground hover:text-primary transition-colors">
            <Trash2 className="h-4 w-4" strokeWidth={2.25} />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function GameLibrary() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [game, setGame] = useState(null);
  const [clips, setClips] = useState(null);
  const [filter, setFilter] = useState("all");
  const [activeKey, setActiveKey] = useState(null);
  const [urls, setUrls] = useState({ endzone: "", sideline: "" });
  const [videoLoading, setVideoLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [newIds, setNewIds] = useState(new Set());
  const [downloaded, setDownloaded] = useState(new Set());
  const [downloading, setDownloading] = useState(false);
  const [dlProgress, setDlProgress] = useState(0);
  const warmedRef = useRef(new Set());
  const clipBlobs = useRef(new Map());
  const maxNumRef = useRef(0);
  const filterRef = useRef(filter);
  const firstLoadRef = useRef(true);

  useEffect(() => { filterRef.current = filter; }, [filter]);

  // group clips into plays (endzone + sideline paired by play_number)
  const plays = useMemo(() => {
    if (!clips) return null;
    const map = new Map();
    for (const c of clips) {
      const key = c.play_number != null ? `p${c.play_number}` : `c${c.clip_number}`;
      const num = c.play_number != null ? c.play_number : c.clip_number;
      if (!map.has(key)) map.set(key, { key, play_number: num, endzone: null, sideline: null, clips: [] });
      const g = map.get(key);
      if (c.view === "endzone") g.endzone = c; else g.sideline = c;
      g.clips.push(c);
    }
    const arr = [...map.values()].sort((a, b) => a.play_number - b.play_number);
    for (const g of arr) {
      const rep = g.endzone || g.sideline;
      g.unit = rep.unit; g.quarter = rep.quarter;
      g.starred = g.clips.some((c) => c.starred);
      g.note = g.endzone?.note || g.sideline?.note || "";
      g.newest = Math.max(...g.clips.map((c) => c.clip_number));
    }
    return arr;
  }, [clips]);

  const activePlay = plays && activeKey != null ? plays.find((p) => p.key === activeKey) : null;
  const activeIndex = plays && activeKey != null ? plays.findIndex((p) => p.key === activeKey) : -1;

  const loadGame = useCallback(async () => {
    try {
      const { data } = await api.get(`/games/${gameId}`);
      setGame(data);
    } catch {
      toast.error("Game not found");
      navigate("/");
    }
  }, [gameId, navigate]);

  const fetchClips = useCallback(async (f) => {
    const params = { game_id: gameId };
    if (f !== "all") params.unit = f;
    const { data } = await api.get("/clips", { params });
    return data;
  }, [gameId]);

  const loadClips = useCallback(async () => {
    try {
      const data = await fetchClips(filter);
      setClips(data);
      const max = data.reduce((m, c) => Math.max(m, c.clip_number), 0);
      if (firstLoadRef.current) { maxNumRef.current = max; firstLoadRef.current = false; }
    } catch { setClips([]); }
  }, [fetchClips, filter]);

  useEffect(() => { loadGame(); }, [loadGame]);
  useEffect(() => { loadClips(); }, [loadClips]);

  // Live Sideline Feed: poll for new clips
  useEffect(() => {
    const id = setInterval(async () => {
      try {
        const data = await fetchClips(filterRef.current);
        setClips(data);
        const max = data.reduce((m, c) => Math.max(m, c.clip_number), 0);
        if (max > maxNumRef.current) {
          const fresh = data.filter((c) => c.clip_number > maxNumRef.current);
          maxNumRef.current = max;
          setNewIds((s) => { const n = new Set(s); fresh.forEach((c) => n.add(c.id)); return n; });
          fresh.forEach((c) => toast.success(`New clip #${c.clip_number}`, {
            description: `${c.view === "endzone" ? "Endzone" : "Sideline"} · ${c.unit} · Q${c.quarter}`,
            icon: <Radio className="h-4 w-4 text-primary" />,
          }));
          fresh.forEach((c) => setTimeout(() => setNewIds((s) => { const n = new Set(s); n.delete(c.id); return n; }), 8000));
          loadGame();
        }
      } catch {}
    }, 4000);
    return () => clearInterval(id);
  }, [fetchClips, loadGame]);

  const clipUrl = useCallback((clip) => clipBlobs.current.get(clip.id) || `${API}/clips/${clip.id}/video`, []);

  // Download a clip into an in-app blob cache so it plays instantly later (and
  // survives the ingress 'no-store' that would otherwise bypass the HTTP cache).
  const warm = useCallback(async (clip) => {
    if (!clip || clipBlobs.current.has(clip.id) || warmedRef.current.has(clip.id)) return;
    warmedRef.current.add(clip.id);
    try {
      const res = await fetch(`${API}/clips/${clip.id}/video`, { credentials: "include" });
      if (!res.ok) throw new Error("fetch failed");
      const blob = await res.blob();
      clipBlobs.current.set(clip.id, URL.createObjectURL(blob));
      setDownloaded((s) => { const n = new Set(s); n.add(clip.id); return n; });
    } catch { warmedRef.current.delete(clip.id); }
  }, []);

  useEffect(() => () => { clipBlobs.current.forEach((u) => URL.revokeObjectURL(u)); }, []);

  const openPlay = (play) => {
    setActiveKey(play.key);
    setNoteDraft(play.note || "");
    const next = { endzone: "", sideline: "" };
    for (const v of ["endzone", "sideline"]) if (play[v]) next[v] = clipUrl(play[v]);
    setUrls(next);
    // progressive playback starts immediately; prefetch this + next play for seamless nav
    play.clips.forEach(warm);
    const idx = plays ? plays.findIndex((p) => p.key === play.key) : -1;
    if (idx >= 0 && plays[idx + 1]) plays[idx + 1].clips.forEach(warm);
  };

  const downloadGame = async () => {
    if (!plays || plays.length === 0) return;
    setDownloading(true);
    setDlProgress(0);
    const all = plays.flatMap((p) => p.clips);
    let done = 0;
    for (const c of all) {
      await warm(c);
      done += 1;
      setDlProgress(Math.round((done / all.length) * 100));
    }
    setDownloading(false);
    toast.success("Game downloaded — every play now loads instantly");
  };

  const goPrev = () => { if (activeIndex > 0) openPlay(plays[activeIndex - 1]); };
  const goNext = () => { if (activeIndex >= 0 && activeIndex < plays.length - 1) openPlay(plays[activeIndex + 1]); };

  const toggleStar = async (play) => {
    const val = !play.starred;
    try {
      await Promise.all(play.clips.map((c) => api.patch(`/clips/${c.id}`, { starred: val })));
      toast.success(val ? `Play #${play.play_number} starred` : "Star removed");
      loadClips();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const saveNote = async () => {
    if (!activePlay) return;
    setSavingNote(true);
    try {
      await Promise.all(activePlay.clips.map((c) => api.patch(`/clips/${c.id}`, { note: noteDraft })));
      toast.success("Note saved");
      loadClips();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingNote(false); }
  };

  const deletePlay = async (play) => {
    try {
      await Promise.all(play.clips.map((c) => api.delete(`/clips/${c.id}`)));
      toast.success(`Play #${play.play_number} deleted`);
      if (activeKey === play.key) { setActiveKey(null); setUrls({ endzone: "", sideline: "" }); }
      loadClips();
      loadGame();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const exportHudl = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/games/${gameId}/export`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(game?.name || "game").replace(/[^a-z0-9]+/gi, "_")}_hudl.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Game exported — ready to upload to Hudl");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Nothing to export yet"); }
    finally { setExporting(false); }
  };

  const singleClip = activePlay ? (activePlay.endzone || activePlay.sideline) : null;
  const singleClipDisplay = singleClip ? { ...singleClip, clip_number: activePlay.play_number } : null;
  const singleUrl = activePlay ? (activePlay.endzone ? urls.endzone : urls.sideline) : "";
  const isDual = activePlay && activePlay.endzone && activePlay.sideline;

  return (
    <div className="min-h-screen bg-background">
      <TopBar />
      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-6">
        <button onClick={() => navigate("/")} data-testid="library-back-button"
          className="flex items-center gap-2 text-sm font-bold uppercase tracking-widest text-muted-foreground hover:text-white transition-colors mb-4">
          <ArrowLeft className="h-4 w-4" strokeWidth={2.5} /> All games
        </button>

        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2">
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-secondary">Film Room</p>
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-primary/15 text-primary text-[10px] font-bold uppercase tracking-widest">
                <Radio className="h-3 w-3 rec-dot" strokeWidth={3} /> Live feed
              </span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl font-black uppercase tracking-tighter leading-none mt-1">{game?.name || "Loading…"}</h1>
            {game?.opponent && <p className="text-muted-foreground mt-1">vs {game.opponent}</p>}
          </div>
          <div className="flex gap-2">
            <Button onClick={() => navigate(`/record/${gameId}`)} data-testid="library-record-button"
              variant="outline" className="h-11 border-border bg-card hover:bg-accent font-heading font-bold uppercase tracking-wide">
              <Video className="h-4 w-4 mr-1.5" strokeWidth={2.5} /> Record
            </Button>
            <Button onClick={downloadGame} data-testid="download-game-button" disabled={downloading}
              variant="outline" className="h-11 border-secondary/60 bg-card hover:bg-accent text-secondary font-heading font-bold uppercase tracking-wide">
              {downloading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <DownloadCloud className="h-4 w-4 mr-1.5" strokeWidth={2.5} />}
              {downloading ? `Downloading ${dlProgress}%` : "Download game"}
            </Button>
            <Button onClick={exportHudl} data-testid="export-hudl-button" disabled={exporting}
              className="h-11 bg-success hover:bg-green-500 text-black font-heading font-bold uppercase tracking-wide transition-colors">
              {exporting ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Download className="h-4 w-4 mr-1.5" strokeWidth={2.5} />}
              Export to Hudl
            </Button>
          </div>
        </div>

        {/* player */}
        {activePlay && (
          <div className="mb-6 fade-up bg-card border border-border rounded-md overflow-hidden">
            {isDual ? (
              <DualClipPlayer play={activePlay} urls={urls} loading={videoLoading}
                onPrev={goPrev} onNext={goNext} hasPrev={activeIndex > 0} hasNext={activeIndex < plays.length - 1} />
            ) : (
              <ClipPlayer clip={singleClipDisplay} videoUrl={singleUrl} loading={videoLoading}
                onPrev={goPrev} onNext={goNext} hasPrev={activeIndex > 0} hasNext={activeIndex < plays.length - 1} />
            )}
            <div className="px-4 py-3 space-y-3">
              <div className="flex items-center gap-3 flex-wrap">
                <span className={`h-8 px-2.5 rounded flex items-center font-heading text-base font-black ${activePlay.unit === "offense" ? "bg-primary text-white" : "bg-secondary text-black"}`}>#{activePlay.play_number}</span>
                <span className="text-sm font-bold uppercase tracking-widest">{activePlay.unit} · Q{activePlay.quarter}</span>
                <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
                  {activePlay.sideline && <span className="flex items-center gap-1"><Flag className="h-3.5 w-3.5" strokeWidth={2.5} />Sideline</span>}
                  {activePlay.endzone && <span className="flex items-center gap-1"><Goal className="h-3.5 w-3.5" strokeWidth={2.5} />Endzone</span>}
                </span>
                <button data-testid="play-star" onClick={() => toggleStar(activePlay)}
                  className={`ml-auto flex items-center gap-1.5 text-sm font-bold uppercase tracking-wide transition-colors ${activePlay.starred ? "text-yellow-400" : "text-muted-foreground hover:text-yellow-400"}`}>
                  <Star className={`h-4 w-4 ${activePlay.starred ? "fill-yellow-400" : ""}`} strokeWidth={2.25} />
                  {activePlay.starred ? "Starred" : "Star play"}
                </button>
              </div>
              {isDual && <p className="text-xs text-muted-foreground">Tap either angle to enlarge it; tap again to go back to split view. Both angles stay in sync.</p>}
              <div className="flex items-center gap-2">
                <StickyNote className="h-4 w-4 text-secondary shrink-0" strokeWidth={2.25} />
                <Input data-testid="clip-note-input" value={noteDraft} onChange={(e) => setNoteDraft(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && saveNote()}
                  placeholder="Add a quick note for the team…" className="h-10 bg-background border-border" />
                <Button data-testid="save-note-button" onClick={saveNote} disabled={savingNote}
                  className="h-10 bg-secondary hover:bg-cyan-300 text-black font-heading font-bold uppercase tracking-wide shrink-0">
                  {savingNote ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" strokeWidth={3} />}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* filters */}
        <div className="flex items-center gap-2 mb-5">
          {FILTERS.map((f) => {
            const Icon = f.icon;
            const on = filter === f.key;
            const color = f.key === "offense" ? "primary" : f.key === "defense" ? "secondary" : "white";
            return (
              <button key={f.key} data-testid={`filter-${f.key}`} onClick={() => setFilter(f.key)}
                className={`h-10 px-4 rounded-md border-2 flex items-center gap-2 font-heading font-bold uppercase tracking-wide transition-all duration-200 ${
                  on
                    ? color === "primary" ? "bg-primary border-primary text-white"
                    : color === "secondary" ? "bg-secondary border-secondary text-black"
                    : "bg-white border-white text-black"
                    : "bg-card border-border text-muted-foreground hover:border-muted-foreground/50"}`}>
                <Icon className="h-4 w-4" strokeWidth={2.5} /> {f.label}
              </button>
            );
          })}
        </div>

        {/* grid */}
        {plays === null ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
        ) : plays.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-border rounded-md">
            <Film className="h-12 w-12 text-muted-foreground mx-auto mb-4" strokeWidth={1.5} />
            <h3 className="font-heading text-2xl font-bold uppercase tracking-tight">No {filter !== "all" ? filter : ""} plays yet</h3>
            <p className="text-muted-foreground mt-2">Plays appear here in real time the moment the recorder hits stop.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {plays.map((p, i) => (
              <div key={p.key} className="fade-up" style={{ animationDelay: `${Math.min(i, 8) * 30}ms` }}>
                <PlayCard play={p} active={activeKey === p.key}
                  isNew={p.clips.some((c) => newIds.has(c.id))}
                  downloaded={p.clips.length > 0 && p.clips.every((c) => downloaded.has(c.id))}
                  onClick={() => openPlay(p)} onDelete={deletePlay} onStar={toggleStar} />
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
